// Commission payout with correction netting: ONE shared helper used by BOTH
// Mark Paid sites (admin Commission tab modal and the rep-detail sub-tab modal).
//
// Sequence (Topic D section 8):
//   1. fresh-read the rep's open corrections (and the complete paged payments)
//   2. net = invoice commission being paid + the selected open corrections
//      (a negative net never settles: corrections stay OPEN, carried forward)
//   3. CLAIM the corrections (compare-and-set open -> settled) BEFORE inserting
//   4. insert the payment rows with the SAME batch_id and UNCHANGED payload
//   5. on insert failure release the claim (awaited, logged); the legacy
//      per-browser localStorage fallback is NEVER allowed when corrections are
//      included (fallbackAllowed:false), the error is shown instead.
// A correction-only payout (no invoice rows) writes NO rep_commission_payments
// rows; its batch id is corr-<repId>-<ts>.
// A payout WITHOUT corrections calls markInvoicesCommissionPaid(rows) exactly
// as before and nothing else.
import { markInvoicesCommissionPaid, listCorrections, listCommissionPaymentsPaged, claimCorrectionsForBatch, releaseCorrectionClaim } from "./supabase";
import { correctionsForRep, openCorrections, netPayout, undoBlockers, round2 } from "./commissionCorrections";

const defaultDeps = { markInvoicesCommissionPaid, listCorrections, listCommissionPaymentsPaged, claimCorrectionsForBatch, releaseCorrectionClaim };

export function correctionOnlyBatchId(repId, ts) {
  return `corr-${repId}-${ts === undefined ? Date.now() : ts}`;
}

const invoiceCommissionOf = (rows) => (rows || []).reduce((s, r) => {
  const n = Number(r && r.commission_amount);
  return s + (Number.isFinite(n) ? n : 0);
}, 0);

/**
 * @param {object} a
 *   repId, rows (the UNCHANGED rep_commission_payments payload, may be []),
 *   batchId (the batch id the caller already builds for `rows`),
 *   method, notes, admin {id,email}, selectedCorrectionIds (ids the admin ticked),
 *   now (optional ms timestamp, tests)
 * @returns
 *   { ok:true, mode:'plain'|'netted'|'corrections_only', batchId, count?, net?,
 *     settled?:[rows], carriedForward?:[rows], negative?:bool }
 *   { ok:false, stage, error, fallbackAllowed:bool, released?:bool }
 */
export async function runCommissionPayout(a = {}, deps = defaultDeps) {
  const rows = Array.isArray(a.rows) ? a.rows : [];
  const ids = Array.from(new Set((a.selectedCorrectionIds || []).filter((x) => x != null)));

  // No corrections in play: byte-for-byte the pre-existing behaviour.
  if (ids.length === 0) {
    const res = await deps.markInvoicesCommissionPaid(rows);
    if (res && res.ok) return { ok: true, mode: "plain", batchId: a.batchId, count: res.count };
    return { ok: false, stage: "insert", error: (res && res.error) || "insert failed", fallbackAllowed: true };
  }

  const repId = String(a.repId || "");
  if (!repId) {
    return { ok: false, stage: "input", error: "This rep has no id, so corrections cannot be settled.", fallbackAllowed: false };
  }

  // 1. fresh reads (never the modal's cached list)
  let corr; let paid;
  try {
    [corr, paid] = await Promise.all([deps.listCorrections({ repId }), deps.listCommissionPaymentsPaged({ repId })]);
  } catch (e) {
    console.warn("[runCommissionPayout] fresh read threw for rep", repId, ":", e?.message || e);
    return { ok: false, stage: "read", error: e?.message || String(e), fallbackAllowed: false };
  }
  if (!corr || !corr.ok) {
    console.warn("[runCommissionPayout] corrections read failed for rep", repId, ":", corr && corr.error);
    return { ok: false, stage: "read", error: (corr && corr.error) || "corrections could not be read", fallbackAllowed: false };
  }
  if (!paid || !paid.ok) {
    console.warn("[runCommissionPayout] payments read failed for rep", repId, ":", paid && paid.error);
    return { ok: false, stage: "read", error: (paid && paid.error) || "payments could not be read", fallbackAllowed: false };
  }
  const live = openCorrections(correctionsForRep(corr.rows, { repId, paidRows: paid.rows }));
  const byId = new Map(live.map((c) => [String(c.id), c]));
  const selected = ids.map((id) => byId.get(String(id)));
  if (selected.some((c) => !c)) {
    return { ok: false, stage: "stale", error: "The corrections changed since this dialog opened (one was already settled, waived or is no longer valid). Close it and open Mark paid again.", fallbackAllowed: false };
  }

  // 2. netting
  const invoiceCommission = invoiceCommissionOf(rows);
  const np = netPayout({ invoiceCommission, selectedCorrections: selected });
  if (np.negative) {
    // Corrections stay OPEN as a balance carried forward; invoices (if any) are paid as usual.
    if (rows.length === 0) {
      return { ok: false, stage: "negative", error: "The corrections total " + np.correctionsTotal.toFixed(2) + " and there is nothing else to pay, so they stay open as a balance carried forward.", fallbackAllowed: false };
    }
    const res = await deps.markInvoicesCommissionPaid(rows);
    if (res && res.ok) return { ok: true, mode: "plain", batchId: a.batchId, count: res.count, negative: true, carriedForward: np.carriedForward, net: np.net };
    // No correction was touched, so the pre-existing fallback stays legal here.
    return { ok: false, stage: "insert", error: (res && res.error) || "insert failed", fallbackAllowed: true };
  }

  // 3. claim BEFORE the payment rows
  const correctionsOnly = rows.length === 0;
  const settleBatchId = correctionsOnly ? correctionOnlyBatchId(repId, a.now) : a.batchId;
  let claim;
  try {
    claim = await deps.claimCorrectionsForBatch(np.settleIds, {
      batchId: settleBatchId, method: a.method, notes: a.notes,
      by: (a.admin && (a.admin.email || a.admin.id)) || null, repId,
      at: new Date(a.now === undefined ? Date.now() : a.now).toISOString(),
    });
  } catch (e) {
    console.warn("[runCommissionPayout] claim failed for rep", repId, "batch", settleBatchId, ":", e?.message || e);
    return { ok: false, stage: "claim", error: e?.message || String(e), fallbackAllowed: false };
  }

  if (correctionsOnly) {
    return { ok: true, mode: "corrections_only", batchId: settleBatchId, net: np.net, settled: claim.rows };
  }

  // 4. payment rows (same batch id, unchanged payload)
  let res;
  try {
    res = await deps.markInvoicesCommissionPaid(rows);
  } catch (e) {
    console.warn("[runCommissionPayout] insert threw for batch", settleBatchId, ":", e?.message || e);
    res = { ok: false, error: e?.message || String(e) };
  }
  if (res && res.ok) {
    return { ok: true, mode: "netted", batchId: settleBatchId, count: res.count, net: np.net, settled: claim.rows };
  }
  // 5. release the claim, awaited and logged; never the localStorage fallback
  console.warn("[runCommissionPayout] payment insert failed for batch", settleBatchId, ":", res && res.error, "; releasing the correction claim");
  const rel = await deps.releaseCorrectionClaim(np.settleIds, settleBatchId);
  if (!rel.ok) console.warn("[runCommissionPayout] claim release incomplete for batch", settleBatchId, ":", rel.error);
  return {
    ok: false, stage: "insert", error: (res && res.error) || "insert failed", fallbackAllowed: false, released: !!rel.ok,
    releaseError: rel.ok ? undefined : rel.error,
  };
}

/**
 * Undo scope from the paid rows the UI already holds: { invoiceIds, batchIds }
 * for the given invoice ids (batch ids of EVERY paid row of those invoices, plus
 * any extra batch ids the caller knows). No rep id is involved.
 */
export function undoScopeFromRows(paidRows, invoiceIds, extraBatchIds = []) {
  const ids = Array.from(new Set((invoiceIds || []).map((x) => String(x)).filter(Boolean)));
  const idSet = new Set(ids);
  // A synthetic "__no_batch__..." id (a paid row without a batch id) is not a real batch.
  const batches = new Set((extraBatchIds || []).map((x) => String(x)).filter((x) => x && !x.startsWith("__no_batch__")));
  (paidRows || []).forEach((r) => {
    // A paid row without a batch id contributes "" (a correction copies it as an empty paid_batch_id).
    if (r && idSet.has(String(r.invoice_id))) batches.add(r.batch_id ? String(r.batch_id) : "");
  });
  return { invoiceIds: ids, batchIds: Array.from(batches) };
}

/**
 * Undo guard for invoice payouts (single invoice or a whole batch). Rules (see
 * undoBlockers in commissionCorrections.js): refused while a correction is
 * settled against the payout, was settled IN the payout's batch, or is still
 * OPEN and anchored to the payout.
 *
 * scope = { invoiceIds, batchIds } supplied by every caller (derived from the
 * paid rows the UI holds, see undoScopeFromRows). The guard NEVER filters by rep
 * id (paid rows are matched to a rep by id OR name and a correction copies the
 * PAID row's rep_id, so a rep filter can miss one, e.g. after a reassignment).
 *
 * Cost and failure model:
 *   - Corrections are read once (two small .in filters: invoice_id in
 *     invoiceIds, settled_batch_id in batchIds, merged and deduped). NO
 *     payments read: anchoring compares each correction's (invoice_id,
 *     paid_batch_id) with the (invoice, batch) scope being undone.
 *   - Fails CLOSED only when the corrections read itself fails. missingTable or
 *     an empty result lets the undo proceed.
 * It is check-then-act (see the PR limitations): another admin can settle a
 * correction between this check and the delete.
 * Returns { ok:true } | { ok:false, message }.
 */
export async function checkUndoAllowed(scope, deps = defaultDeps) {
  const sc = scope || {};
  const invoiceIds = Array.from(new Set((sc.invoiceIds || []).map((x) => String(x)).filter(Boolean)));
  if (invoiceIds.length === 0) return { ok: true };
  const batchIds = Array.from(new Set((sc.batchIds || []).map((x) => String(x)).filter(Boolean)));
  let corr;
  try {
    corr = await deps.listCorrections({ invoiceIds, batchIds });
  } catch (e) {
    console.warn("[checkUndoAllowed] corrections read threw:", e?.message || e);
    return { ok: false, message: "Could not verify commission corrections, so the undo was not done: " + (e?.message || e) };
  }
  if (!corr || !corr.ok) {
    console.warn("[checkUndoAllowed] corrections read failed:", corr && corr.error);
    return { ok: false, message: "Could not verify commission corrections, so the undo was not done: " + ((corr && corr.error) || "read failed") };
  }
  if (corr.missingTable || !Array.isArray(corr.rows) || corr.rows.length === 0) return { ok: true };
  const g = undoBlockers(corr.rows, { invoiceIds, batchIds: sc.batchIds || [] });
  if (!g.ok) return { ok: false, message: g.message };
  return { ok: true };
}

// Human message for a payout result (both Mark Paid sites show the same text).
export function describePayoutResult(r) {
  if (!r) return "";
  if (r.ok) {
    if (r.mode === "corrections_only") return "Corrections settled: net payout $" + round2(r.net).toFixed(2) + " (no invoice rows were written).";
    if (r.negative) return "Invoices paid. The corrections were NOT settled because the net would be negative; they stay open as a balance carried forward.";
    return "";
  }
  let m = "Could not record the payout: " + r.error + ".";
  if (r.stage === "insert" && r.fallbackAllowed === false) {
    m += r.released
      ? " Nothing was paid and the corrections are open again."
      : " The corrections could not be re-opened automatically (" + (r.releaseError || "unknown") + "). Refresh the Commission tab and check them before paying again.";
  }
  return m;
}
